import styled from "styled-components";

const AnalyticsProcessWrapper = styled.div`
  --brand: #035184;
  --brand-dark: #023a63;
  --brand-light: #e8f4fd;
  --ink: #0f172a;
  --ink-muted: #64748b;
  --ink-faint: #94a3b8;
  --border: #e2e8f0;
  --surface: #ffffff;
  --surface-alt: #f8fafc;
  --success: #10b981;
  --warning: #f59e0b;
  --danger: #ef4444;
  --radius: 10px;
  --radius-sm: 6px;

  font-family: system-ui, -apple-system, sans-serif;
  color: var(--ink);

  /* ── Seção: Plan Hero ── */
  .plan-hero {
    position: relative;
    overflow: hidden;
    border-radius: var(--radius);
    background: linear-gradient(135deg, #0a1e35 0%, #035184 55%, #1067b9 100%);
    padding: 1.5rem 1.75rem;
    margin-bottom: 1.25rem;
    color: white;
  }

  .plan-hero::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
    background-size: 32px 32px;
    pointer-events: none;
  }

  .plan-hero__content {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .plan-hero__badge {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  .plan-hero__icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(255,255,255,0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    flex-shrink: 0;
  }

  .plan-hero__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    opacity: 0.65;
    margin-bottom: 2px;
  }

  .plan-hero__name {
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: white;
    margin: 0;
  }

  .plan-hero__stats {
    display: flex;
    gap: 2rem;
    flex-wrap: wrap;
  }

  .plan-hero__stat {
    text-align: center;
  }

  .plan-hero__stat-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.6;
    display: block;
    margin-bottom: 2px;
  }

  .plan-hero__stat-value {
    font-size: 1rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: white;
  }

  .plan-hero__upgrade {
    padding: 0.45rem 1.1rem;
    border-radius: 20px;
    border: 1.5px solid rgba(255,255,255,0.6);
    background: transparent;
    color: white;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
    white-space: nowrap;
  }

  .plan-hero__upgrade:hover {
    background: rgba(255,255,255,0.15);
    border-color: white;
  }

  /* ── Seção genérica ── */
  .dash-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 1.25rem 1.5rem;
    margin-bottom: 1.25rem;
    transition: box-shadow 0.2s;
  }

  .dash-section:hover {
    box-shadow: 0 4px 20px rgba(3, 81, 132, 0.08);
  }

  .dash-section__title {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 700;
    color: var(--ink-faint);
    margin: 0 0 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid var(--border);
  }

  /* ── Métricas de uso ── */
  .usage-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0.875rem;
  }

  .usage-card {
    position: relative;
    padding: 0.875rem 1rem;
    border-radius: var(--radius-sm);
    background: var(--surface-alt);
    border: 1px solid var(--border);
    border-left: 3px solid var(--border);
    transition: border-left-color 0.2s, transform 0.15s;
  }

  .usage-card:hover {
    transform: translateY(-1px);
  }

  .usage-card--ok { border-left-color: var(--success); }
  .usage-card--warn { border-left-color: var(--warning); }
  .usage-card--crit { border-left-color: var(--danger); }
  .usage-card--neutral { border-left-color: var(--brand); }

  .usage-card__icon {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  .usage-card__icon--ok { color: var(--success); }
  .usage-card__icon--warn { color: var(--warning); }
  .usage-card__icon--crit { color: var(--danger); }
  .usage-card__icon--neutral { color: var(--brand); }

  .usage-card__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-faint);
    margin-bottom: 4px;
  }

  .usage-card__value {
    font-size: 1.05rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: var(--ink);
    line-height: 1.1;
    margin-bottom: 0.5rem;
  }

  .usage-card__bar {
    height: 3px;
    border-radius: 2px;
    background: var(--border);
    overflow: hidden;
  }

  .usage-card__bar-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .usage-card__bar-fill--ok { background: var(--success); }
  .usage-card__bar-fill--warn { background: var(--warning); }
  .usage-card__bar-fill--crit { background: var(--danger); }
  .usage-card__bar-fill--neutral { background: var(--brand); }

  /* ── Tipo de projeto ── */
  .project-type-list {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  }

  .project-type-row {
    display: grid;
    grid-template-columns: 28px 1fr auto auto;
    align-items: center;
    gap: 0.625rem;
  }

  .project-type-row__icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
  }

  .project-type-row__name {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--ink);
  }

  .project-type-row__bar-wrap {
    height: 4px;
    border-radius: 2px;
    background: var(--border);
    overflow: hidden;
    grid-column: 2 / -2;
    margin-top: 2px;
    display: none;
  }

  .project-type-row__meta {
    font-size: 0.7rem;
    color: var(--ink-faint);
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .project-type-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .project-type-item__header {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: space-between;
  }

  .project-type-item__left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .project-type-item__name {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--ink);
  }

  .project-type-item__right {
    font-size: 0.72rem;
    color: var(--ink-faint);
    font-variant-numeric: tabular-nums;
  }

  .project-type-item__track {
    height: 3px;
    border-radius: 2px;
    background: var(--border);
    overflow: hidden;
  }

  .project-type-item__fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* Filtros de métrica */
  .metric-toggle {
    display: flex;
    gap: 2px;
    background: var(--surface-alt);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 2px;
  }

  .metric-toggle__btn {
    padding: 0.25rem 0.75rem;
    font-size: 0.72rem;
    font-weight: 500;
    border: none;
    border-radius: 18px;
    background: transparent;
    color: var(--ink-muted);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    white-space: nowrap;
  }

  .metric-toggle__btn--active {
    background: var(--brand);
    color: white;
  }

  /* ── Conversões ── */
  .conversion-item {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--border);
  }

  .conversion-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .conversion-item__icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  .conversion-item__info {
    flex: 1;
    min-width: 0;
  }

  .conversion-item__name {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 4px;
  }

  .conversion-item__track {
    height: 4px;
    border-radius: 2px;
    background: var(--border);
    overflow: hidden;
    margin-bottom: 3px;
  }

  .conversion-item__fill {
    height: 100%;
    border-radius: 2px;
    background: var(--brand);
    transition: width 0.5s;
    animation: pulse-bar 2s ease-in-out infinite;
  }

  @keyframes pulse-bar {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }

  .conversion-item__status {
    font-size: 0.7rem;
    color: var(--ink-faint);
  }

  .conversion-item__pct {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--brand);
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }

  /* ── Projetos recentes ── */
  .recent-project-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0;
    border-bottom: 1px solid var(--border);
    cursor: default;
    transition: background 0.12s;
    border-radius: 4px;
    margin: 0 -4px;
    padding-left: 4px;
    padding-right: 4px;
  }

  .recent-project-item:last-child {
    border-bottom: none;
  }

  .recent-project-item:hover {
    background: var(--surface-alt);
  }

  .recent-project-item__thumb {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
  }

  .recent-project-item__thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .recent-project-item__info {
    flex: 1;
    min-width: 0;
  }

  .recent-project-item__name {
    font-size: 0.84rem;
    font-weight: 600;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .recent-project-item__type {
    font-size: 0.7rem;
    color: var(--ink-faint);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .recent-project-item__date {
    font-size: 0.72rem;
    color: var(--ink-faint);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* ── Timeline de atividade ── */
  .activity-item {
    display: flex;
    gap: 0.75rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border);
  }

  .activity-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .activity-item__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--brand);
    margin-top: 6px;
    flex-shrink: 0;
  }

  .activity-item__body {
    flex: 1;
    min-width: 0;
  }

  .activity-item__label {
    font-size: 0.82rem;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .activity-item__label strong {
    font-weight: 600;
  }

  .activity-item__meta {
    font-size: 0.7rem;
    color: var(--ink-faint);
    margin-top: 1px;
  }

  /* ── Estado vazio ── */
  .empty-state {
    text-align: center;
    padding: 2rem 1rem;
    color: var(--ink-faint);
  }

  .empty-state i {
    font-size: 1.75rem;
    display: block;
    margin-bottom: 0.5rem;
    opacity: 0.5;
  }

  .empty-state p {
    font-size: 0.82rem;
    margin: 0;
  }

  /* ── Dois painéis lado a lado ── */
  .two-col {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
    margin-bottom: 1.25rem;
    align-items: start;
  }

  @media (max-width: 768px) {
    .two-col {
      grid-template-columns: 1fr;
    }

    .usage-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .plan-hero__stats {
      gap: 1rem;
    }
  }

  /* ── Botão link ── */
  .btn-link-brand {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--brand);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-decoration: none;
    letter-spacing: 0.02em;
    transition: color 0.15s;
  }

  .btn-link-brand:hover {
    color: var(--brand-dark);
    text-decoration: underline;
  }

  /* ── Animação de entrada ── */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .dash-section,
  .plan-hero {
    animation: fadeUp 0.4s ease-out both;
  }

  .dash-section:nth-child(2) { animation-delay: 0.05s; }
  .dash-section:nth-child(3) { animation-delay: 0.10s; }
  .dash-section:nth-child(4) { animation-delay: 0.15s; }
  .dash-section:nth-child(5) { animation-delay: 0.20s; }
`;

export default AnalyticsProcessWrapper;
